import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Statistic, Tag, Typography } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

const { Text } = Typography

interface Row {
  work_order_id: string
  work_order_no: string
  machine_id: string | null
  department_id: string | null
  maintenance_type: string
  priority: string
  status: string
  spare_cost: number
  labour_cost: number
  vendor_cost: number
  consumable_cost: number
  downtime_cost: number
  total_cost: number
  root_cause_id: string | null
  technician_id: string | null
  cost_date: string
  closed_at: string | null
  // Phase 3.1 — backend-calculated per-WO cost/kg
  production_kg: number | null
  cost_per_kg: number | null
  cost_per_kg_match_level: string
}

/**
 * Spec §9 — Maintenance cost report. Read-only — cost fields are not
 * editable (operator can't enter cost; backend calculates).
 */
export default function MaintenanceCostReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { machineCode, deptName, failureReasonName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({
    plant_id: plantId,
    from_date: dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
    to_date: dayjs().format('YYYY-MM-DD'),
  })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-cost', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.cost(filters) as unknown as Promise<Row[]>,
  })
  const rows = data ?? []
  const total = rows.reduce((a, r) => a + (r.total_cost ?? 0), 0)
  const spareC = rows.reduce((a, r) => a + (r.spare_cost ?? 0), 0)
  const labourC = rows.reduce((a, r) => a + (r.labour_cost ?? 0), 0)
  const dtC = rows.reduce((a, r) => a + (r.downtime_cost ?? 0), 0)

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/cost', filters,
        `maintenance-cost-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const moneyCol = (title: string, key: keyof Row) => ({
    title, dataIndex: key as string, key: key as string, width: 120,
    render: (v: number) => `₹ ${(v ?? 0).toFixed(2)}`,
  })

  const columns = [
    { title: 'WO', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    {
      title: 'Machine', key: 'm', width: 140,
      render: (_: unknown, r: Row) => machineCode(r.machine_id),
    },
    {
      title: 'Department', key: 'd', width: 150,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    {
      title: 'Type', dataIndex: 'maintenance_type', key: 'mt', width: 110,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'pr', width: 100,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'st', width: 130,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    moneyCol('Spare', 'spare_cost'),
    moneyCol('Labour', 'labour_cost'),
    moneyCol('Vendor', 'vendor_cost'),
    moneyCol('Consum.', 'consumable_cost'),
    moneyCol('Downtime', 'downtime_cost'),
    {
      title: 'Total',
      dataIndex: 'total_cost',
      key: 'tc',
      width: 130,
      render: (v: number) => <Text strong>₹ {v.toFixed(2)}</Text>,
    },
    // Phase 3.1 — Cost per kg (backend-calculated, read-only)
    {
      title: 'Production kg', dataIndex: 'production_kg', key: 'pk', width: 130,
      render: (v: number | null) => v == null ? <Text type="secondary">—</Text> : v.toFixed(2),
    },
    {
      title: 'Cost / kg', dataIndex: 'cost_per_kg', key: 'cpk', width: 160,
      render: (v: number | null, r: Row) => v == null
        ? <Text type="secondary">—</Text>
        : <Text strong>₹ {v.toFixed(4)} <Text type="secondary">({r.cost_per_kg_match_level})</Text></Text>,
    },
    {
      title: 'Root cause', key: 'rc', width: 200,
      render: (_: unknown, r: Row) => failureReasonName(r.root_cause_id),
    },
    {
      title: 'Closed', dataIndex: 'closed_at', key: 'cl', width: 130,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM') : '—',
    },
  ]

  return (
    <div>
      <PageHeader title="Maintenance Cost"
        subtitle="Spare + Labour + Vendor + Consumable + Downtime cost per work order. Backend-computed." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ dateRange: true, department: true, machine: true, maintenanceType: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Work orders" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Total cost" value={total} prefix="₹" precision={2} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Spare cost" value={spareC} prefix="₹" precision={2} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Labour cost" value={labourC} prefix="₹" precision={2} /></Card></Col>
      </Row>
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Downtime cost" value={dtC} prefix="₹" precision={2} /></Card></Col>
        <Col xs={12} md={18}>
          <Card>
            <Text type="secondary">
              Cost fields are calculated by maintenance_cost_service and rendered as <b>read-only</b> here.
              Operators never type cost — the motto: ENTER ONCE → AUTO COST → AUTO DASHBOARD.
            </Text>
          </Card>
        </Col>
      </Row>
      <DataTable rowKey="work_order_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
